import { factorsViewSlice, DEFAULT_FACTORS_VIEW_STATE, FACTORS_VIEW_PREFERENCE_BASE, FACTORS_VIEW_PREFERENCE_FIELDS, selectFactorsViewPanel } from "../store/factors-view-slice"
import { createSyncPreferencesHook } from "./create-sync-preferences-hook"

const useSyncFactorsViewPreferencesBase = createSyncPreferencesHook({
  selectPanelState: selectFactorsViewPanel,
  defaultState: DEFAULT_FACTORS_VIEW_STATE,
  preferenceBase: FACTORS_VIEW_PREFERENCE_BASE,
  preferenceFields: FACTORS_VIEW_PREFERENCE_FIELDS,
  setPreferences: (dashboardId, panelId, prefs) =>
    factorsViewSlice.actions.setPreferences({ dashboardId, panelId, ...prefs }),
  clearPendingClonePersist: (dashboardId, panelId) =>
    factorsViewSlice.actions.clearPendingClonePersist({ dashboardId, panelId }),
})

interface UseSyncFactorsViewPreferencesOptions {
  /** Fields seeded from module config — legacy fallback must not overwrite these. */
  omitFallbackKeys?: string[]
  /** Source panel of a duplicate. When `clonePanelState` found no live source state (source never
   *  mounted), hydrate from the source's persisted "factors-view.{duplicateFromPanelId}" key instead
   *  of the legacy base (which holds the original primary's prefs, not the source panel's). */
  duplicateFromPanelId?: string
}

/**
 * Per-panel sync. Called from FactorsViewWrapper, not from the dashboard-level
 * useDashboardPreferencesSync, because preferences are scoped per factors-view panel.
 *
 * Legacy users have settings saved under the un-suffixed "factors-view" subtype. The base hook's
 * fallback reads those on first mount so the migration is seamless; subsequent saves go under
 * "factors-view.{panelId}".
 */
export function useSyncFactorsViewPreferences(
  { omitFallbackKeys, duplicateFromPanelId }: UseSyncFactorsViewPreferencesOptions = {},
): boolean {
  return useSyncFactorsViewPreferencesBase(({ panelId, wasInitializedAtMount }) => {
    const duplicateFallbackEnabled = Boolean(duplicateFromPanelId && panelId && !wasInitializedAtMount)
    return {
      // Override the default base fallback only for an unmounted-source duplicate; otherwise the
      // factory's default (base when fresh) applies.
      ...(duplicateFallbackEnabled && { fallbackSubType: `${FACTORS_VIEW_PREFERENCE_BASE}.${duplicateFromPanelId}` }),
      ...(omitFallbackKeys && omitFallbackKeys.length > 0 && { omitFallbackKeys }),
    }
  })
}
